import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { LogOut, LayoutDashboard, FileImage, History, Users, Image as ImageIcon, Languages as LangIcon, BarChart3, ScrollText, Activity, Images, Settings, Sparkles } from "lucide-react";
import { BRAND } from "@/lib/brand";

const Logo = () => (
  <div className="flex items-center gap-2.5" data-testid="brand-logo">
    <img src={BRAND.logo} alt={BRAND.companyShort} className="h-10 w-auto max-w-[150px] object-contain" />
    <div className="leading-tight">
      <div className="font-display font-bold text-brand-ink text-sm">{BRAND.companyShort}</div>
      <div className="text-[11px] uppercase tracking-[0.18em] text-brand-600">{BRAND.productName}</div>
    </div>
  </div>
);

const NavItem = ({ to, icon: Icon, children, active, testid }) => (
  <Link
    to={to}
    data-testid={testid}
    className={
      "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors " +
      (active
        ? "bg-brand-600 text-white shadow-sm shadow-brand-600/30"
        : "text-slate-600 hover:bg-brand-50 hover:text-brand-700")
    }
  >
    <Icon className="w-4 h-4" strokeWidth={1.75} />
    <span>{children}</span>
  </Link>
);

export default function Shell({ children, role }) {
  const { user, logout } = useAuth();
  const loc = useLocation();
  const nav = useNavigate();

  const handleLogout = async () => {
    await logout();
    nav("/login", { replace: true });
  };

  const isActive = (p) => loc.pathname === p || (p !== "/admin" && p !== "/mr" && loc.pathname.startsWith(p));

  const adminNav = (
    <>
      <NavItem to="/admin" icon={LayoutDashboard} active={loc.pathname === "/admin"} testid="nav-admin-dashboard">Dashboard</NavItem>
      <NavItem to="/admin/generate" icon={Sparkles} active={isActive("/admin/generate")} testid="nav-admin-generate">Generate Poster</NavItem>
      <NavItem to="/admin/employees" icon={Users} active={isActive("/admin/employees")} testid="nav-admin-employees">Employees</NavItem>
      <NavItem to="/admin/templates" icon={ImageIcon} active={isActive("/admin/templates")} testid="nav-admin-templates">Templates</NavItem>
      <NavItem to="/admin/languages" icon={LangIcon} active={isActive("/admin/languages")} testid="nav-admin-languages">Languages</NavItem>
      <NavItem to="/admin/posters" icon={Images} active={isActive("/admin/posters")} testid="nav-admin-posters">Poster Records</NavItem>
      <NavItem to="/admin/reports" icon={BarChart3} active={isActive("/admin/reports")} testid="nav-admin-reports">Reports</NavItem>
      <NavItem to="/admin/audit" icon={ScrollText} active={isActive("/admin/audit")} testid="nav-admin-audit">Audit Log</NavItem>
      <NavItem to="/admin/settings" icon={Settings} active={isActive("/admin/settings")} testid="nav-admin-settings">Settings</NavItem>
    </>
  );

  const mrNav = (
    <>
      <NavItem to="/mr" icon={LayoutDashboard} active={loc.pathname === "/mr"} testid="nav-mr-dashboard">Dashboard</NavItem>
      <NavItem to="/mr/generate" icon={FileImage} active={isActive("/mr/generate")} testid="nav-mr-generate">Generate Poster</NavItem>
      <NavItem to="/mr/history" icon={History} active={isActive("/mr/history")} testid="nav-mr-history">History</NavItem>
    </>
  );

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col md:flex-row">
      {/* Sidebar */}
      <aside className="w-full md:w-64 md:min-h-screen bg-white border-b md:border-b-0 md:border-r border-slate-200 flex md:flex-col" data-testid="app-sidebar">
        <div className="p-5 md:py-7 md:px-6 flex-1 md:flex-none flex md:block items-center justify-between md:space-y-1">
          <Logo />
        </div>
        <nav className="hidden md:flex flex-col gap-1 px-4 pb-6 flex-1">
          {role === "admin" ? adminNav : mrNav}
        </nav>
        {/* Mobile horizontal nav */}
        <nav className="md:hidden flex gap-1 overflow-x-auto px-4 pb-3 no-scrollbar">
          {role === "admin" ? adminNav : mrNav}
        </nav>
        <div className="hidden md:block border-t border-slate-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-brand-600 grid place-items-center text-white font-bold text-sm">
              {user?.name?.[0] || "U"}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-slate-900 truncate" data-testid="user-name">{user?.name}</div>
              <div className="text-xs text-slate-500 truncate" data-testid="user-emp-code">{user?.emp_code}</div>
            </div>
            <Button variant="ghost" size="icon" onClick={handleLogout} data-testid="logout-button">
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0 fade-in" data-testid="app-main">
        <div className="md:hidden border-b border-slate-200 px-4 py-2 flex items-center justify-between bg-white">
          <div className="text-sm">
            <span className="font-semibold text-slate-900">{user?.name}</span>
            <span className="text-slate-500 ml-2">· {user?.emp_code}</span>
          </div>
          <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="logout-button-mobile">
            <LogOut className="w-4 h-4" />
          </Button>
        </div>
        <div className="px-4 py-6 md:px-10 md:py-10 max-w-7xl mx-auto">
          {children}
        </div>
      </main>
    </div>
  );
}
